import React, { useEffect, useState, useRef } from 'react';
import { getCountriesList, getCountryTravelAdvice } from '../services/apiService';

// Medixs brand colors
// Orange: #F47920  Purple: #6B21A8  (as seen on medixs.nl)

const ADVICE = {
  rood:   { dot: 'bg-red-500',    bar: 'bg-red-500',    badge: 'bg-red-50 text-red-700 border-red-200',    label: 'Rood — Niet reizen' },
  oranje: { dot: 'bg-[#F47920]',  bar: 'bg-[#F47920]',  badge: 'bg-orange-50 text-[#F47920] border-orange-200', label: 'Oranje — Alleen noodzakelijk' },
  geel:   { dot: 'bg-yellow-400', bar: 'bg-yellow-400', badge: 'bg-yellow-50 text-yellow-700 border-yellow-200', label: 'Geel — Wees waakzaam' },
  groen:  { dot: 'bg-green-500',  bar: 'bg-green-500',  badge: 'bg-green-50 text-green-700 border-green-200',  label: 'Groen — Veilig reizen' },
};

function detectColor(intro) {
  if (!intro) return null;
  const t = intro.toLowerCase();
  if (t.includes('rood')) return 'rood';
  if (t.includes('oranje')) return 'oranje';
  if (t.includes('geel')) return 'geel';
  if (t.includes('groen')) return 'groen';
  return null;
}

export default function Dashboard() {
  const [countries, setCountries] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [mapZoom, setMapZoom] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    getCountriesList().then(setCountries).catch(console.error);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = countries.filter((c) =>
    c.location.toLowerCase().includes(query.toLowerCase())
  );

  const handleSelect = async (country) => {
    setQuery(country.location);
    setOpen(false);
    setDetails(null);
    setError(null);
    setActiveTab(0);
    setLoading(true);
    try {
      const data = await getCountryTravelAdvice(country.isocode);
      setDetails(data);
    } catch {
      setError('Reisadvies kon niet worden geladen voor dit land.');
    } finally {
      setLoading(false);
    }
  };

  const color = details ? detectColor(details.introduction) : null;
  const adv = color ? ADVICE[color] : null;
  const mapImage = details?.files?.find(f => f.mapType === 'standard');

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Open Sans', Arial, sans-serif" }}>

      {/* Top bar - zoals medixs.nl */}
      <div className="bg-white border-b border-gray-100 text-xs text-gray-500 py-2 px-6 flex items-center gap-4">
        <span>✉ keuring@me.com</span>
        <span>|</span>
        <span>☎ +31 6 24823350</span>
      </div>

      {/* Navigatie header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div>
            <div className="text-lg font-bold text-gray-800">
              <span style={{ color: '#F47920' }}>Medixs</span> Medische Diensten
            </div>
            <div className="text-xs text-gray-400">Snel & Goed gekeurd, gevaccineerd, geadviseerd</div>
          </div>
          <nav className="flex items-center gap-1">
            <a href="https://www.medixs.nl" target="_blank" rel="noreferrer"
              className="text-sm text-gray-600 hover:text-gray-800 px-3 py-1.5 rounded transition-colors">
              Home
            </a>
            <span className="text-sm px-3 py-1.5 rounded font-medium text-white"
              style={{ backgroundColor: '#F47920' }}>
              Reisadvies
            </span>
            <a href="https://www.medixs.nl/vaccinatie-centrum" target="_blank" rel="noreferrer"
              className="text-sm text-gray-600 hover:text-gray-800 px-3 py-1.5 rounded transition-colors">
              Travel Clinic
            </a>
          </nav>
        </div>
      </header>

      {/* Pagina hero */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex items-start justify-between gap-6">
            <div>
              <h1 className="text-2xl font-bold">
                <span style={{ color: '#F47920' }}>Officieel reisadvies per land</span>{' '}
                <span style={{ color: '#6B21A8' }}>· Ministerie van Buitenlandse Zaken</span>
              </h1>
            </div>
            <div className="text-right shrink-0">
              <div className="text-2xl font-bold" style={{ color: '#F47920' }}>{countries.length}</div>
              <div className="text-xs text-gray-400">landen beschikbaar</div>
            </div>
          </div>
          <div className="mt-4 flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 w-full">
            <span className="text-blue-400 mt-0.5 shrink-0">ℹ</span>
            <p className="text-gray-800 text-base leading-relaxed">
              Dit is het <strong>officiële veiligheidsreisadvies</strong> van het Ministerie van Buitenlandse Zaken — géén medisch reisadvies. Het advies geeft per land aan hoe veilig het is om naartoe te reizen, uitgedrukt in een kleurcode:{' '}
              <span style={{background:'#16a34a',color:'#fff',borderRadius:'4px',padding:'1px 7px',fontWeight:600,fontSize:'0.85em'}}>groen</span> veilig reizen &nbsp;
              <span style={{background:'#eab308',color:'#fff',borderRadius:'4px',padding:'1px 7px',fontWeight:600,fontSize:'0.85em'}}>geel</span> wees waakzaam &nbsp;
              <span style={{background:'#F47920',color:'#fff',borderRadius:'4px',padding:'1px 7px',fontWeight:600,fontSize:'0.85em'}}>oranje</span> alleen noodzakelijke reizen &nbsp;
              <span style={{background:'#dc2626',color:'#fff',borderRadius:'4px',padding:'1px 7px',fontWeight:600,fontSize:'0.85em'}}>rood</span> niet reizen.{' '}
              Het advies is gebaseerd op de actuele veiligheidssituatie, zoals politieke onrust, criminaliteit, natuurrampen of gezondheidsrisico's ter plaatse. Voor persoonlijk medisch reisadvies en vaccinaties kunt u bij ons terecht.
            </p>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">

        {/* Zoekbalk */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: '#6B21A8' }}>
            Land zoeken
          </h2>
          <div className="relative" ref={dropdownRef}>
            <div className="relative">
              <svg className="absolute left-4 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                type="text"
                className="w-full pl-11 pr-10 py-3 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 text-sm focus:outline-none focus:border-orange-400"
                style={{ '--tw-ring-color': '#F47920' }}
                placeholder="Typ een landnaam, bijv. Spanje..."
                value={query}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); setDetails(null); }}
                onFocus={(e) => { e.target.select(); setOpen(true); }}
              />
              {query && (
                <button
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={() => { setQuery(''); setOpen(false); setDetails(null); setError(null); }}
                  tabIndex={-1}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {open && filtered.length > 0 && (
              <ul className="absolute z-30 w-full bg-white border border-gray-200 rounded-lg mt-1 max-h-64 overflow-y-auto shadow-lg divide-y divide-gray-100">
                {filtered.map((country) => (
                  <li
                    key={country.id}
                    className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-orange-50 transition-colors"
                    onClick={() => handleSelect(country)}
                  >
                    <span className="text-sm font-medium text-gray-700">{country.location}</span>
                    <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded">{country.isocode}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Laden */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-10 h-10 border-4 border-gray-200 rounded-full animate-spin mx-auto mb-4"
              style={{ borderTopColor: '#F47920' }} />
            <p className="text-gray-500 text-sm">Reisadvies ophalen...</p>
          </div>
        )}

        {/* Fout */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3 items-start">
            <span className="text-red-500">⚠</span>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Landinformatie */}
        {details && !loading && (
          <div className="space-y-5">

            {/* Advies-kleur balk bovenaan hero */}
            {adv && <div className={`h-1.5 w-full rounded-full ${adv.bar}`} />}

            {/* Hero kaart */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 flex flex-col md:flex-row gap-6">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold" style={{ color: '#6B21A8' }}>
                    {details.location}
                  </h2>
                  <p className="text-gray-400 text-xs mt-1">{details.modificationdate}</p>

                  {adv && (
                    <span className={`inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded border text-xs font-semibold ${adv.badge}`}>
                      <span className={`w-2 h-2 rounded-full ${adv.dot}`} />
                      {adv.label}
                    </span>
                  )}

                  {details.introduction && (
                    <div
                      className="prose mt-4 text-sm text-gray-600 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: details.introduction }}
                    />
                  )}
                </div>

                {mapImage && (
                  <div className="shrink-0">
                    <img
                      src={mapImage.fileurl}
                      alt={`Reisadvieskaart ${details.location}`}
                      className="w-full md:w-56 rounded-lg border border-gray-200 shadow-sm object-cover cursor-zoom-in hover:opacity-90 transition-opacity"
                      onClick={() => setMapZoom(true)}
                      title="Klik om te vergroten"
                    />
                    <p className="text-center text-xs text-gray-400 mt-1">Klik om te vergroten</p>
                  </div>
                )}
              </div>
            </div>

            {/* Statistieken */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Land', value: details.location },
                { label: 'ISO-code', value: details.isocode },
                { label: 'Reisadvies', value: color ? color.charAt(0).toUpperCase() + color.slice(1) : '–' },
                { label: 'Categorieën', value: details.content?.length ?? 0 },
              ].map((stat) => (
                <div key={stat.label} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
                  <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#6B21A8' }}>{stat.label}</p>
                  <p className="text-lg font-bold text-gray-800 mt-1 truncate">{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Tabbladen */}
            {details.content && details.content.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="flex overflow-x-auto border-b border-gray-200">
                  {details.content.map((cat, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveTab(i)}
                      className="px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all"
                      style={activeTab === i
                        ? { borderColor: '#F47920', color: '#F47920', backgroundColor: '#FFF8F3' }
                        : { borderColor: 'transparent', color: '#6B7280' }
                      }
                    >
                      {cat.category}
                    </button>
                  ))}
                </div>

                <div className="p-6 divide-y divide-gray-100">
                  {details.content[activeTab].contentblocks.map((block, j) => (
                    <div key={j} className="py-5 first:pt-0 last:pb-0">
                      <h4 className="font-semibold mb-2 text-sm" style={{ color: '#6B21A8' }}>
                        {block.paragraphtitle}
                      </h4>
                      <div
                        className="prose text-sm text-gray-600 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: block.paragraph }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lege staat */}
        {!details && !loading && !error && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-16 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 text-3xl"
              style={{ backgroundColor: '#FFF4EB' }}>
              🗺️
            </div>
            <h3 className="font-bold text-gray-700 text-lg mb-1" style={{ color: '#6B21A8' }}>
              Zoek een land
            </h3>
            <p className="text-gray-400 text-sm">Selecteer een land hierboven om het officiële reisadvies te bekijken</p>
            <div className="mt-6 pt-6 border-t border-gray-100 text-xs text-gray-400">
              Informatie afkomstig van het Ministerie van Buitenlandse Zaken · {countries.length} landen beschikbaar
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-gray-400 py-4 border-t border-gray-200">
          <span style={{ color: '#F47920', fontWeight: 600 }}>Medixs</span> Medische Diensten ·
          St. Antonius Ziekenhuis, Woerden · Zorgpassage 15
        </div>

      </main>

      {/* Kaart zoom modal */}
      {mapZoom && mapImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setMapZoom(false)}
        >
          <div className="relative max-w-4xl w-full mx-4" onClick={e => e.stopPropagation()}>
            <button
              className="absolute -top-10 right-0 text-white text-sm font-medium hover:opacity-70 transition-opacity"
              onClick={() => setMapZoom(false)}
            >
              ✕ Sluiten
            </button>
            <img
              src={mapImage.fileurl}
              alt={`Reisadvieskaart ${details.location}`}
              className="w-full rounded-lg shadow-2xl"
            />
            <p className="text-center text-white/50 text-xs mt-3">{details.location} — Reisadvieskaart</p>
          </div>
        </div>
      )}
    </div>
  );
}
